import { UserProfile } from "@clerk/clerk-react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { LanguageKey } from "../../../../../../../i18n/index.ts";
import { useLanguage } from "../../../../../../../providers/language-provider.tsx";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/settings/")({
	component: SettingsPage,
});

// Align Clerk's UserProfile with the Signal Light system: brand colors,
// tight radius, General Sans, and a flat card (no ambient shadow).
const clerkAppearance = {
	variables: {
		colorPrimary: "oklch(60% 0.22 252)",
		colorText: "oklch(20% 0.012 250)",
		colorTextSecondary: "oklch(38% 0.015 250)",
		colorBackground: "oklch(98.5% 0.005 250)",
		colorInputBackground: "oklch(98.5% 0.005 250)",
		colorInputText: "oklch(20% 0.012 250)",
		borderRadius: "0.375rem",
		fontFamily: '"General Sans", system-ui, -apple-system, sans-serif',
	},
	elements: {
		rootBox: "w-full",
		cardBox: "w-full shadow-none border border-base-300",
		card: "shadow-none",
		navbar: "border-r border-base-300",
	},
} as const;

function SettingsPage() {
	const { t } = useTranslation();
	const { currentLanguage, changeLanguage, languages } = useLanguage();

	return (
		<div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
			<header className="mb-6">
				<p className="font-mono text-xs uppercase tracking-wide text-base-content/55">
					{t("nav.settings")}
				</p>
				<h1
					data-testid="settings-title"
					className="mt-1 text-2xl font-semibold tracking-tight text-base-content"
				>
					{t("settings.title")}
				</h1>
			</header>
			<div className="space-y-6">
				<section
					data-testid="settings-language-card"
					className="rounded-box border border-base-300 bg-base-100 p-5 max-w-md"
				>
					<h2 className="text-sm font-semibold text-base-content">{t("settings.language")}</h2>
					<select
						data-testid="settings-language-select"
						className="select select-bordered w-full mt-3"
						value={currentLanguage}
						onChange={(e) => changeLanguage(e.target.value as LanguageKey)}
					>
						{Object.entries(languages).map(([key, lang]) => (
							<option key={key} value={key}>
								{lang.nativeName}
							</option>
						))}
					</select>
				</section>
				<section data-testid="settings-profile-card">
					<h2 className="text-sm font-semibold text-base-content mb-3">{t("settings.profile")}</h2>
					<div className="overflow-x-auto">
						<UserProfile routing="hash" appearance={clerkAppearance} />
					</div>
				</section>
			</div>
		</div>
	);
}
